import type { Pt } from "../math/polygon";
import type { WarningCode, WarningSeverity } from "../warnings";
import type { RcDesignResult } from "../rc";

export type CaseId = "static" | "seismicUp" | "seismicDown";
export const CASE_LABEL: Record<CaseId, string> = { static: "Statik", seismicUp: "Depremli (1−kv)", seismicDown: "Depremli (1+kv)" };

export type ForceKind = "weight" | "wedge" | "surchargeOnWedge" | "earthStatic" | "earthDynamic" | "lineLoad" | "water" | "waterDynamic" | "uplift" | "inertia" | "passive" | "frontWater";

export interface Force {
  id: string;
  label: string;
  kind: ForceKind;
  V: number;      // aşağı +
  H: number;      // devirici +
  x: number;      // etkime noktası (mutlak)
  y: number;
  armX: number;   // moment kolu (x − x0)
  armY: number;   // (y − y0)
  Mres: number;   // max(V,0)·armX
  Movr: number;   // max(H,0)·armY + max(−V,0)·armX
  note?: string;
}

export interface CaseSums { V: number; U: number; N: number; Hdrive: number; Hresist: number; Rpk: number; Mres: number; Movr: number }

export interface CheckResult {
  /** Yönetmelik doğrulaması (TBDY: γR'li direnç; EC7: hükmeden kombinasyon; ABD: yok). Alan adı geriye uyum için "tbdy". */
  tbdy?: { effect: number; resistance: number; gammaR: number; designResistance: number; ratio: number; ok: boolean; verdict: string; combination?: string };
  classic: { effect: number; resistance: number; factor: number; target: number; ok: boolean; verdict: string; /** hüküm vermez, bilgi amaçlı (TBDY, classicGoverns false) */ informative?: boolean };
}

/** EC7 kısmi katsayılı bir kombinasyonun sonucu (tasarım etkileri/dirençleri). */
export interface EcCheck { Ed: number; Rd: number; ratio: number; ok: boolean; note: string }
export interface EcCombinationResult {
  id: string; label: string; limitState: "GEO" | "EQU";
  factors: { gG: number; gGfav: number; gQ: number; gPhi: number; gC: number; gRh: number; gRv: number; gRe: number };
  phiD: number; cD: number;
  overturning: EcCheck; sliding: EcCheck; bearing: EcCheck;
  Vd: number; Hd: number; eD: number; BeffD: number;
}

/**
 * Dış merkezlilik. Eğik tabanda (inclined) x̄, e, B, B' taban düzlemi boyunca ölçülür (B = taban boyu B_yatay/cosα);
 * N' = N·cosα + ΣH·sinα taban düzlemine dik, T' = ΣH·cosα − N·sinα taban boyunca (burun yönü +).
 */
export interface EccentricityResult {
  xbar: number; e: number; limit: number; limitDivisor: number; ok: boolean; B: number; Beff: number;
  inclined?: { slope: number; alphaDeg: number; N: number; T: number; length: number };
}
export interface BasePressureResult { qToe: number; qHeel: number; contactLength: number; qMeyerhof: number; tensionRedistributed: boolean }
/** EN 1998-5 Ek F sismik taşıma gücü (bilgi amaçlı; hüküm TBDY'de) — yalnız depremli durumlarda ve yöntem seçiliyse */
export interface BearingEc8Result {
  soil: "cohesive" | "cohesionless";
  Nmax: number; Ngamma?: number; phiDdeg?: number; cuD?: number;
  Nbar: number; Vbar: number; Mbar: number; Fbar: number; NbarLimit: number;
  /** (F.1) sol tarafı; f < 0 → yüzeyin içinde */
  f: number; ok: boolean;
  gammaRd: number; gammaRdCategory: string; agS: number;
  /** Girdi etkileri (kN/m, kNm/m) ve temel genişliği */
  N: number; V: number; M: number; B: number;
  notes: string[];
}
export interface BearingResult extends CheckResult { qk: number; sigmaAllow: number; qkSource: "given" | "computed"; ec8?: BearingEc8Result }

export interface LayerCoeff { layer: number; label: string; phi: number; delta: number; theta: number; Kstatic: number; Ktotal: number; Kdyn: number; branch: "a" | "b"; /** back-face yönteminde kademe (1 = tepe) */ face?: number }

export interface LoadCaseResult {
  id: CaseId;
  kh: number; kv: number; kvFactor: number;
  coefficients: { perLayer: LayerCoeff[]; Kp?: number };
  forces: Force[];
  sums: CaseSums;
  overturning: CheckResult;
  sliding: CheckResult;
  eccentricity: EccentricityResult;
  basePressure: BasePressureResult;
  bearing: BearingResult;
  /** EC7 profilinde kombinasyon ayrıntıları */
  ec?: EcCombinationResult[];
  allOk: boolean;
}

export interface JointCaseResult { N: number; V: number; M: number; e: number; eLimit: number; sigmaMax: number; sigmaMin: number; eOk: boolean; compressionOk: boolean; shearFactor: number; shearTarget: number; shearOk: boolean; ok: boolean; verdict: string }
export interface JointResult { level: number; label: string; width: number; cases: Partial<Record<CaseId, JointCaseResult>>; allOk: boolean }

export interface GeometryResult {
  polygon: Pt[]; area: number; centroid: Pt; B: number; toe: Pt; heel: Pt; yTop: number;
  virtualPlane: { alphaDeg: number; psiDeg: number; top: Pt };
  wedge: Pt[]; wedgeArea: number; Hp: number; embedmentDepth: number;
  pressureMethod: "virtual-plane" | "back-face";
  /** back-face yönteminde kademe yüzleri (üstten alta); itki diyagramı ve rapor için */
  backFaces: { index: number; bottom: Pt; top: Pt; psiDeg: number; yC: number }[];
  /** Betonarme kayma dişi çokgeni (taban altında), varsa */
  key?: Pt[];
  /** Eğik taban tanα (topuk alçak), yalnız > 0 ise; B yatay izdüşüm, toe.y − heel.y = B·tanα */
  baseSlope?: number;
}

export interface SeismicResult {
  /** tbdy: kh = 0,4·SDS/r, kv = 0,5·kh · direct: kh, kv kullanıcıdan · ec8: α·S/r · aashto: 0,5·FPGA·PGA (SDS yalnız tbdy) */
  source: "tbdy" | "direct" | "ec8" | "aashto";
  SS?: number; S1?: number; FS?: number; F1?: number; SDS?: number; SD1?: number; r: number; kh: number; kv: number;
  /** hidrodinamik su artımı katsayısı: tbdy'de 0,4·SDS (Denk. 16.20), direct'te kh */
  khWater: number;
  allowedDisplacementMm: number | null; thetaUpDeg: number; thetaDownDeg: number;
}

export interface TraceStep { id: string; title: string; formula: string; substituted: string; result: string; unit: string; ref?: string; caseId?: CaseId }
export interface EngineWarning { code: WarningCode; severity: WarningSeverity; message: string; context?: string }

export interface WallResult {
  wallId: string; wallName: string; engineVersion: string;
  /** Yönetmelik profili (kontrol sütunu başlığı ve atıflar için) */
  designCode: "tbdy2018" | "ec7" | "us"; codeLabel: string;
  /** Yapı tipi: taş ağırlık · betonarme konsol · payandalı */
  structureType: "masonry-gravity" | "rc-cantilever" | "rc-counterfort";
  ok: boolean;                       // tüm kontroller tüm durumlarda sağlandı
  warnings: EngineWarning[];
  geometry: GeometryResult;
  seismic: SeismicResult | null;
  cases: Partial<Record<CaseId, LoadCaseResult>>;
  governingSeismic: CaseId | null;
  joints: JointResult[];
  /** Betonarme kesit tasarımı (yalnız rc-cantilever modunda) */
  rc?: RcDesignResult;
  trace: TraceStep[];
}

export interface ProjectResult { projectId: string; walls: WallResult[]; ok: boolean }

// ── Toptan göçme (şev stabilitesi) ─────────────────────────────────────────
export type SlopeMethod = "bishop" | "fellenius";
export interface SlipCircle { cx: number; cy: number; r: number }
/** Kritik dairenin bir dilimi (çizim ve rapor tablosu için) */
export interface SlopeSlice {
  x: number; yBase: number; ySurf: number; width: number;
  W: number;          // dilim ağırlığı (+ sürşarj) kN/m
  alphaDeg: number;   // taban eğimi (merkezin sağında +)
  phiDeg: number; c: number; u: number;
  material: "backfill" | "foundation" | "wall";
}
export interface GlobalStabilityResult {
  caseId: "static" | "seismic";
  method: SlopeMethod;
  kh: number;
  fsMin: number;
  circle: SlipCircle;
  entry: Pt; exit: Pt;
  slices: SlopeSlice[];
  /** Kritik dairede itici ve kaydırıcı moment bileşenleri (R'ye bölünmüş, kN/m) */
  driving: number; resisting: number;
  target: number; ok: boolean; verdict: string;
  /** Denenen geçerli daire sayısı */
  searched: number;
  iterations: number;
}
export interface GlobalStabilityAnalysis {
  static: GlobalStabilityResult;
  seismic?: GlobalStabilityResult;
  method: SlopeMethod;
  grid: { xRange: [number, number]; yRange: [number, number]; step: number; nx: number; ny: number };
  radiusStep: number; minDepthBelowBase: number; slices: number;
  elapsedMs: number;
  ok: boolean;
}
